import { useRef, useState, useEffect } from 'react';
import type {
  FC,
  ChangeEvent,
  MouseEvent,
  KeyboardEvent as ReactKeyboardEvent,
  FormEvent
} from 'react';
import { Text, Editor, Transforms } from 'slate';
import type { Range } from 'slate';
import { ReactEditor, useSlate } from 'slate-react';
import styled from 'styled-components';

import {
  Button,
  CardContent,
  Grid,
  Icon,
  registerIcon,
  Input,
  TouchDialog,
  Popover,
  useOuterEvent,
  useElement,
  Form,
  useI18n,
  isTouchDevice,
  defaultThemeProp
} from '@pega/cosmos-react-core';
import type { ForwardProps } from '@pega/cosmos-react-core';
import * as chainIcon from '@pega/cosmos-react-core/lib/components/Icon/icons/chain.icon';

import { useRTEContext } from '../RichTextEditor.context';
import EditorCommands from '../utils/EditorCommands';

import ToolbarButton from './ToolbarButton';
import { getKeyCommand } from './utils';

registerIcon(chainIcon);

const StyledTouchDialogContent = styled(CardContent)(
  () => `
  flex: 1;

  > div {
    display: flex;
    flex-direction: column;
    min-height: 100%;
  }

  > form {
    display: flex;
    flex-direction: column;
    min-height: 100%;
  }

  > form > div:not([aria-hidden='true']):nth-last-child(2) {
    margin-top: auto;
  }
`
);

StyledTouchDialogContent.defaultProps = defaultThemeProp;

interface AnchorButtonProps {
  osx: boolean;
}

const AnchorButton: FC<AnchorButtonProps & ForwardProps> = ({ osx, ...props }) => {
  const t = useI18n();
  const { setPopoverOpen, onEditorFocus } = useRTEContext();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const textInputRef = useRef<HTMLInputElement>(null);
  const urlInputRef = useRef<HTMLInputElement>(null);
  const touchDialogRef = useRef<HTMLDivElement>(null);
  const editor = useSlate();
  const [popoverEl, setPopoverEl] = useElement();
  const [selectedText, setSelectedText] = useState('');
  const [selection, setSelection] = useState<Range | null>(null);
  const [url, setUrl] = useState('');
  const [urlMatch, setUrlMatch] = useState(true);
  const [anchorMenu, setAnchorMenu] = useState(false);
  const tooltip = getKeyCommand(osx, ({ ctrl }) => `${t('rte_link')} (${ctrl}K)`);

  const openMenu = (opts: { focusInput?: boolean } = {}) => {
    setAnchorMenu(true);
    setPopoverOpen(true);
    if (opts.focusInput) {
      setTimeout(() => {
        textInputRef.current?.focus();
      }, 0);
    }
  };

  const resetMenu = (opts: { focusEditor?: boolean } = { focusEditor: true }) => {
    setSelection(null);
    setSelectedText('');
    setUrl('');
    setUrlMatch(true);
    setAnchorMenu(false);
    setPopoverOpen(false);
    if (opts.focusEditor) {
      setTimeout(() => {
        onEditorFocus({
          runAfterFocus: () => {
            if (selection) Transforms.setSelection(editor, selection);
          }
        });
      }, 0);
    }
  };

  useOuterEvent('mousedown', [popoverEl, buttonRef, touchDialogRef], () => {
    if (anchorMenu) {
      resetMenu();
    }
  });

  const createLink = () => {
    if (url) {
      EditorCommands.createLink(
        editor,
        selectedText,
        new URL(/^[a-z][a-z0-9.+-]*:/i.test(url) ? url : `http:${url}`).href,
        selection
      );
      resetMenu();
    }
  };

  useEffect(() => {
    if (editor.selection) {
      setSelectedText(Editor.string(editor, editor.selection));
      setSelection(editor.selection);
      const [match] = Editor.nodes<Text>(editor, {
        match: n => Text.isText(n) && !!n.href,
        at: editor.selection
      });
      if (match) {
        setUrl(match[0].href || '');
        setSelectedText(match[0].text);
      } else {
        setUrl('');
      }
    }
  }, [anchorMenu]);

  const preventDef = (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const isLinkActive = () => {
    const [link] = Editor.nodes<Text>(editor, { match: n => Text.isText(n) && !!n.href });
    return !!link;
  };

  const cancelAnchorCreation = (event?: KeyboardEvent | MouseEvent) => {
    if (
      (event?.type === 'keydown' && (event as KeyboardEvent)?.key === 'Enter') ||
      event?.type === 'mousedown' ||
      !event
    ) {
      event?.preventDefault();
      resetMenu({ focusEditor: false });
      buttonRef.current?.focus();
    }
  };

  useEffect(() => {
    const keyCommandListener = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey) && ReactEditor.isFocused(editor)) {
        e.preventDefault();
        openMenu({ focusInput: true });
      }
      if (e.key === 'Escape') {
        cancelAnchorCreation();
      }
    };

    document.addEventListener('keydown', keyCommandListener);

    return () => {
      document.removeEventListener('keydown', keyCommandListener);
    };
  }, []);

  const anchorFormContent = (
    <Form
      actions={
        <>
          <Button
            variant='secondary'
            onKeyDown={cancelAnchorCreation}
            onMouseDown={cancelAnchorCreation}
            type='button'
          >
            {t('cancel')}
          </Button>
          <Button disabled={!url || !urlMatch} name='apply' type='submit' variant='primary'>
            {t('apply')}
          </Button>
        </>
      }
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        createLink();
      }}
    >
      <Grid container={{ rowGap: 2 }}>
        <Input
          label={t('rte_link_text')}
          value={selectedText}
          onClick={preventDef}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setSelectedText(e.target.value)}
          ref={textInputRef}
        />
        <Input
          label={t('rte_link_url')}
          value={url}
          onClick={preventDef}
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            const urlInput = e.target.value;
            setUrl(urlInput);
            if (!urlMatch) {
              try {
                // eslint-disable-next-line no-new
                new URL(/^[a-z][a-z0-9+.-]*:/i.test(urlInput) ? urlInput : `http:${urlInput}`);
                setUrlMatch(true);
              } catch {
                setUrlMatch(false);
              }
            }
          }}
          onBlur={() => {
            try {
              // eslint-disable-next-line no-new
              new URL(/^[a-z][a-z0-9+.-]*:/i.test(url) ? url : `http:${url}`);
              setUrlMatch(true);
            } catch {
              setUrlMatch(false);
            }
          }}
          info={!urlMatch ? t('rte_invalid_url') : ''}
          status={!urlMatch ? 'error' : undefined}
          ref={urlInputRef}
        />
      </Grid>
    </Form>
  );

  return (
    <>
      <ToolbarButton
        ref={buttonRef}
        onMouseDown={() => {
          openMenu();
        }}
        onKeyDown={(e: ReactKeyboardEvent) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            openMenu({ focusInput: true });
          }
        }}
        active={isLinkActive()}
        tooltip={tooltip}
        label={t('rte_link')}
        {...props}
      >
        <Icon name='chain' />
      </ToolbarButton>
      {isTouchDevice ? (
        <TouchDialog
          ref={touchDialogRef}
          open={anchorMenu}
          heading={t('rte_add_link')}
          onDismiss={() => resetMenu()}
        >
          <StyledTouchDialogContent>{anchorFormContent}</StyledTouchDialogContent>
        </TouchDialog>
      ) : (
        <Popover show={anchorMenu} target={buttonRef.current} ref={setPopoverEl} placement='bottom'>
          <CardContent>{anchorFormContent}</CardContent>
        </Popover>
      )}
    </>
  );
};

export default AnchorButton;
